import React from 'react'
import { Text } from '@sendoutcards/quantum-design-ui'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import {
  AccountFragment,
  DigitalGiftCardForRedemptionFragment,
} from 'src/graphql/generated/graphql'
import {
  useActions,
  useEffect,
  useFlagsWithUseQueries,
  usePersistedStateV2,
  usePersistedUserData,
  useState,
} from 'src/hooks'
import { BasicCreateAccountForm } from 'src/saleor/components/account/checkoutModal/BasicCreateAccountForm'
import { LoginForm } from 'src/saleor/components/account/checkoutModal/LoginForm'
import { Button } from 'src/design_system/components/Button/Button'
import DialogHeader from '../ActSetup/DialogHeader'
import { AddPhoneNumberToAccount } from './AddPhoneNumberToAccount'

export type GiftCardRedemptionDialogProps = {
  giftCard: DigitalGiftCardForRedemptionFragment
  hasJustLinkedToUser: boolean
  hasJustAddedPhoneNumber: boolean
  isLinkingToAccount: boolean
  linkToAccount: () => Promise<string | undefined>
  addPhoneToAccount: () => Promise<string | undefined>
  setHasJustLoggedIn: (v: boolean) => void
  onClose: () => void
}

type CurrentView = 'redeem' | 'createAccount' | 'login' | 'addPhone'

export const GiftCardRedemptionDialog: React.FC<
  GiftCardRedemptionDialogProps
> = props => {
  const {
    giftCard,
    hasJustLinkedToUser,
    hasJustAddedPhoneNumber,
    isLinkingToAccount,
    linkToAccount,
    addPhoneToAccount,
    setHasJustLoggedIn,
    onClose,
  } = props
  const linkedUser = giftCard.recipient.linkedUser
  const {
    account,
    controlData: { isLoggedIn },
  } = usePersistedUserData()
  const actions = useActions()
  const { hasGiftCardWallet } = useFlagsWithUseQueries()
  const [currentView, setCurrentView] = useState<CurrentView>('redeem')
  const [errorMessage, setErrorMessage] = useState<string>()

  const [hasJustCreatedAcc, setHasJustCreatedAcc] = usePersistedStateV2<
    string | undefined
  >('hasCreatedAccount', undefined, true)

  // TODO: this should just live in the inner component...
  const [isSponsorExpanded, setIsSponsorExpanded] = useState(false)

  const linkToAccountInner = async () => {
    const newErrorMessage = await linkToAccount()
    setErrorMessage(newErrorMessage)
  }

  useEffect(() => {
    if (
      hasJustLinkedToUser &&
      !hasJustAddedPhoneNumber &&
      account &&
      !account.verifiedPhoneNumber
    ) {
      setCurrentView('addPhone')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasJustLinkedToUser])

  if (currentView === 'createAccount') {
    return (
      <Dialog
        isOpen={true}
        onClose={() => {
          setCurrentView('redeem')
        }}
        padding="1rem"
        mobileOpenHeight="8px"
        sheetBgColorOverride="#FFF"
        titleBarReplacement={
          <DialogHeader
            onBack={() => setCurrentView('redeem')}
            onClose={onClose}
          />
        }
      >
        <BasicCreateAccountForm
          handleSuccessfullAccCreation={async (_account: AccountFragment) => {
            setCurrentView('redeem')
            // This will make the parent component link the card to the user even after the state reset that happens
            // on login.
            setHasJustLoggedIn(true)
            setHasJustCreatedAcc('true')
          }}
          handleAlreadyUser={() => {
            setCurrentView('login')
          }}
          isSponsorExpanded={isSponsorExpanded}
          handleIsSponsorExpanded={setIsSponsorExpanded}
          shouldStackForm={true}
        />
      </Dialog>
    )
  } else if (currentView === 'login') {
    return (
      <Dialog
        isOpen={true}
        onClose={() => {
          setCurrentView('redeem')
        }}
        padding="1rem"
        mobileOpenHeight="8px"
        sheetBgColorOverride="#FFF"
        titleBarReplacement={
          <DialogHeader
            onBack={() => setCurrentView('redeem')}
            onClose={onClose}
          />
        }
      >
        <LoginForm
          handleNotAUserYet={() => {
            setCurrentView('createAccount')
          }}
          onComplete={async (_account: AccountFragment) => {
            setCurrentView('redeem')
            // This will make the parent component link the card to the user even after the state reset that happens
            // on login.
            setHasJustLoggedIn(true)
          }}
        />
      </Dialog>
    )
  } else if (currentView === 'addPhone') {
    return (
      <AddPhoneNumberToAccount
        addPhoneToAccount={addPhoneToAccount}
        onClose={() => {
          setCurrentView('redeem')
        }}
      />
    )
  }

  const linkAccountBannerContent: {
    message: string
    isError: boolean
    showLinkButton?: boolean
    showWalletLink?: boolean
  } = (() => {
    if (linkedUser) {
      if (hasJustLinkedToUser) {
        if (hasJustCreatedAcc) {
          return {
            message: `Your account has been created and this gift card has been added to your wallet, ${linkedUser.firstName} ${linkedUser.lastName}.`,
            isError: false,
            showWalletLink: isLoggedIn,
          }
        }
        return {
          message: `This gift card has been added to your wallet, ${linkedUser.firstName} ${linkedUser.lastName}.`,
          isError: false,
          showWalletLink: isLoggedIn,
        }
      } else {
        return {
          message: `This gift card is linked to ${linkedUser.firstName} ${linkedUser.lastName}'s wallet.`,
          isError: false,
          showWalletLink: isLoggedIn,
        }
      }
    } else {
      return {
        message: errorMessage
          ? errorMessage
          : 'Save gift card to your account for easy access.',
        isError: !!errorMessage,
        showLinkButton: true,
      }
    }
  })()
  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      padding="0"
      mobileOpenHeight="8px"
      outerWrapperStyle={{
        height: `calc(100dvh - 8px)`,
      }}
      wrapperStyle={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        gap: '0.5rem',
      }}
    >
      <iframe
        title="Gift Card Redemption"
        src={giftCard.payout.url}
        style={{
          width: 'min(100dvw, 1000px)',
          flex: '1 1',
        }}
      />
      {hasGiftCardWallet && (
        <>
          <div
            style={{
              width: '90%',
              height: '2px',
              margin: '0 auto',
              backgroundColor: 'gray',
            }}
          />
          <div
            style={{
              flex: '0 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              margin: '0 1rem 1rem 1rem',
            }}
          >
            <Text
              type="largeBody"
              alignment="center"
              color={
                linkAccountBannerContent.isError ? 'danger' : 'primaryBody'
              }
            >
              {linkAccountBannerContent.message}
            </Text>
            {linkAccountBannerContent.showLinkButton && (
              <Button
                margin="0.7rem 0"
                padding="12px"
                borderRadius="0.5rem"
                backgroundColor="#404040"
                backgroundColorHover="#303030"
                onClick={async () => {
                  if (isLoggedIn) {
                    await linkToAccountInner()
                  } else {
                    setCurrentView('login')
                  }
                }}
                isDisabled={isLinkingToAccount}
              >
                {isLinkingToAccount ? 'Linking...' : 'Add to Wallet'}
              </Button>
            )}
            {linkAccountBannerContent.showWalletLink && (
              <Button
                margin="0.7rem 0"
                padding="12px"
                borderRadius="0.5rem"
                backgroundColor="#404040"
                backgroundColorHover="#303030"
                onClick={() => {
                  actions.openDashboardMyGiftsRoute()
                }}
              >
                View Wallet
              </Button>
            )}
          </div>
        </>
      )}
    </Dialog>
  )
}
